import React, { useState, useEffect } from 'react'
import { Row, Col } from 'reactstrap'
import { useDispatch } from 'react-redux'
import { saveExcludedDomains } from '../actions/backup365ReportsActions'
import './Backup365SummaryCard.css'

const Backup365SummaryCard = ({ reportDetails }) => {
  const dispatch = useDispatch()

  const [activeTab, setActiveTab] = useState('complete')
  const [page, setPage] = useState(1)

  const [domains, setDomains] = useState([])
  const [newDomain, setNewDomain] = useState('')
  const [hasChanges, setHasChanges] = useState(false)

  const itemsPerPage = 10

  useEffect(() => {
    setDomains([...(reportDetails.excludedDomains || [])])
    setHasChanges(false)
  }, [reportDetails])

  /** ============================
   *  DOMAIN VALIDATION + ADD
   *  ============================ */
  const handleAddDomain = () => {
    const trimmed = newDomain.trim().toLowerCase()

    const domainRegex = /^[A-Za-z0-9.-]+$/

    if (!trimmed) {
      alert('Domain cannot be empty.')
      return
    }

    if (trimmed.startsWith('@')) {
      alert('Do not include @ — enter domain like: contoso.com')
      return
    }

    if (!trimmed.includes('.')) {
      alert('Invalid domain. Must contain at least one dot, e.g. contoso.com')
      return
    }

    if (!domainRegex.test(trimmed)) {
      alert('Domain contains invalid characters.')
      return
    }

    if (domains.includes(trimmed)) {
      alert('Domain already exists.')
      return
    }

    setDomains((prev) => {
      setHasChanges(true)
      return [...prev, trimmed]
    })

    setNewDomain('')
    setPage(1)
  }

  /** ============================
   *  TAB DATA
   *  ============================ */
  const getChanges = () => {
    switch (activeTab) {
      case 'excluded':
        return reportDetails.excludedChanges || []
      case 'complete':
        return reportDetails.completeLicenseChanges || []
      case 'mailbox':
        return reportDetails.mailboxLicenseChanges || []
      case 'errors':
        return reportDetails.errors || []
      case 'domains':
        return domains
      default:
        return []
    }
  }

  const items = getChanges()
  const start = (page - 1) * itemsPerPage
  const currentItems = items.slice(start, start + itemsPerPage)
  const totalPages = Math.ceil(items.length / itemsPerPage)

  const renderPagination = () => {
    if (totalPages <= 1) return null
    return (
      <nav>
        <ul className="pagination justify-content-center mb-0">
          {Array.from({ length: totalPages }).map((_, i) => (
            <li key={i} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
              <button className="page-link" onClick={() => setPage(i + 1)}>
                {i + 1}
              </button>
            </li>
          ))}
        </ul>
      </nav>
    )
  }

  const getChangeStyle = (change) => {
    const trimmed = change.trim()
    if (trimmed.startsWith('+')) return 'change-added'
    if (trimmed.startsWith('-')) return 'change-removed'
    return 'change-neutral'
  }

  return (
    <div className="backup365-changes-card mb-4">
      <div className="card shadow-sm border-0">
        <div className="card-body">
          <h5 className="card-title mb-3">Summary</h5>

          {/* TABS */}
          <ul className="nav nav-tabs mb-3">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'complete' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('complete')
                  setPage(1)
                }}
              >
                Complete ({reportDetails.completeLicenseChanges?.length || 0})
              </button>
            </li>

            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'mailbox' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('mailbox')
                  setPage(1)
                }}
              >
                Mailbox ({reportDetails.mailboxLicenseChanges?.length || 0})
              </button>
            </li>

            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'errors' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('errors')
                  setPage(1)
                }}
              >
                Sync Errors ({reportDetails.errors?.length || 0})
              </button>
            </li>

            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'domains' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('domains')
                  setPage(1)
                }}
              >
                Excluded Domains ({domains.length})
              </button>
            </li>
          </ul>

          {/* ================================
              DOMAINS TAB
          ================================= */}
          {activeTab === 'domains' ? (
            <div>
              {/* Add Domain Row */}
              <Row className="mb-3 align-items-center">
                <Col xs="12" md="5">
                  <input
                    type="text"
                    value={newDomain}
                    onChange={(e) => setNewDomain(e.target.value)}
                    className="form-control"
                    placeholder="Enter domain (example: contoso.com) to exclude from reporting"
                  />
                </Col>

                <Col xs="12" md="1" className="mt-2 mt-md-0">
                  <button className="btn btn-primary w-30 domain-btn" onClick={handleAddDomain}>
                    Add
                  </button>
                </Col>
              </Row>

              {/* Domain List */}
              {currentItems.length > 0 ? (
                <ul className="list-group list-group-flush mb-3">
                  {currentItems.map((domain, index) => (
                    <li key={index} className="list-group-item">
                      <Row className="align-items-center">
                        <Col xs="9" md="9">
                          {domain}
                        </Col>
                        <Col md="2"> </Col>
                        <Col xs="1" md="1" className="text-end">
                          <button
                            className="btn btn-danger btn-sm w-30 domain-btn"
                            onClick={() => {
                              setDomains((prev) => {
                                setHasChanges(true)
                                return prev.filter((d) => d !== domain)
                              })
                              setPage(1)
                            }}
                          >
                            Delete
                          </button>
                        </Col>
                      </Row>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted">No excluded domains added.</p>
              )}

              {/* SAVE BUTTON */}
              <Row className="mt-3">
                <Col xs="12" md="3" className="ms-auto">
                  <button
                    className="btn btn-primary w-30"
                    disabled={!hasChanges}
                    onClick={() => {
                      dispatch(saveExcludedDomains(reportDetails.companyId, domains))
                      setHasChanges(false)
                    }}
                  >
                    Save Changes
                  </button>
                </Col>
              </Row>

              {renderPagination()}
            </div>
          ) : (
            /* ================================
                OTHER TABS
            ================================= */
            <>
              <div className="text-muted small mb-2">
                Showing {start + 1}–{Math.min(start + itemsPerPage, items.length)} of {items.length} changes
              </div>

              {currentItems.length > 0 ? (
                <ul className="list-group list-group-flush mb-3">
                  {currentItems.map((change, index) => {
                    const styleClass = getChangeStyle(change)
                    const cleanText =
                      change.startsWith('+') || change.startsWith('-') ? change.substring(1).trim() : change

                    return (
                      <li key={index} className="list-group-item d-flex align-items-center gap-2">
                        <span className={`change-dot ${styleClass}`}> </span>
                        <span>{cleanText}</span>
                      </li>
                    )
                  })}
                </ul>
              ) : activeTab !== 'errors' ? (
                <p className="text-muted">No changes since last month for this license type.</p>
              ) : (
                <p className="text-muted">No sync errors detected.</p>
              )}

              {renderPagination()}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default Backup365SummaryCard
